import { useCallback, useEffect, useRef } from 'react'

import { type Api, logError } from '@subaio/utils'

export const useSignupProof = (api: Api) => {
	const signupProof = useRef<Promise<{ challengeToken: string; challengeSolution: string; expiresAt: number }> | null>(
		null,
	)

	const getSignupProof = useCallback(async () => {
		try {
			let proofPromise = signupProof.current
			if (!proofPromise) {
				proofPromise = (async () => {
					const requestStartedAt = Date.now()
					const challenge = await api.post<{
						challengeToken: string
						nonce: string
						difficulty: number
						expiresIn: number
					}>('/auth/signup/challenge', {})
					const challengeSolution = await new Promise<string>((resolve, reject) => {
						const worker = new Worker(new URL('../containers/SignUp/proofOfWorkWorker.ts', import.meta.url))
						worker.onmessage = ({ data }: MessageEvent<{ solution: string }>) => {
							worker.terminate()
							resolve(data.solution)
						}
						worker.onerror = (error) => {
							worker.terminate()
							reject(error)
						}
						worker.postMessage({ nonce: challenge.nonce, difficulty: challenge.difficulty })
					})
					return {
						challengeToken: challenge.challengeToken,
						challengeSolution,
						expiresAt: requestStartedAt + challenge.expiresIn * 1000 - 1000,
					}
				})()
				signupProof.current = proofPromise
			}
			const proof = await proofPromise
			if (proof.expiresAt <= Date.now()) {
				signupProof.current = null
				return getSignupProof()
			}
			return proof
		} catch (error) {
			signupProof.current = null
			throw error
		}
	}, [api])

	useEffect(() => {
		void getSignupProof().catch(logError)
	}, [getSignupProof])

	return async () => {
		const proof = await getSignupProof()
		signupProof.current = null
		return proof
	}
}
